import { type ReactNode, type RefObject, useEffect, useRef, useState } from 'react';
import { ListFilter, List, Unplug } from 'lucide-react';

import {
    Alert,
    AlertActions,
    AlertContent,
    AlertDescription,
    AlertTitle,
} from '@/components/Alert/Alert';
import { Button } from '@/components/Button/Button';
import {
    Card,
    CardAction,
    CardContent,
    CardCount,
    CardHeader,
    CardTitle,
} from '@/components/Card/Card';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { SearchInput } from '@/components/SearchInput/SearchInput';
import { Pager } from '@/components/Pagination/Pager';
import { Skeleton } from '@/components/Skeleton/Skeleton';
import { TableFooterBar } from '@/components/Table/Table';
import { ROWS_BEFORE_MEASURE, useFittedRows } from '@/hooks/useFittedRows';
import { REGISTER_COPY } from '@/layouts/registerCopy';
import { scrollingAncestor, visibleTop } from '@/util/scroll';

/*
  The register, as the design draws every simple list: one floating card whose header carries
  the title and the actions, a toolbar with the search and its filters inside the card, the
  table inside the card's padding, and the count with the pager under it. Rows per page are as
  many 68px rows as fit under the table's header before the footer bar (52), the card's bottom
  padding (20) and the page's (24), so the register ends where the screen does and nothing
  scrolls on a desk; the design's 8 stands in on phones, where rows are cards.
*/
const ROW_HEIGHT = 68;
const HEADER_ROW = 46;
const FOOTER_BAR = 52;
const RESERVE = HEADER_ROW + FOOTER_BAR + 20 + 24 + 4;

/**
 * Server-side paging, for a table too big to hold: `rows` are one page already, the pager is the
 * caller's, and the register reports back how many rows it measured room for so the caller can ask
 * the server for that many. Omit it and the register pages `rows` itself, as every register does.
 */
export interface ServerPaging {
    /** The page showing, from 1. */
    page: number;
    onPageChange: (page: number) => void;
    /** Rows matching the search and filters, across every page. */
    matched: number;
    /** How many rows fit under the header on this screen. */
    onPageSizeChange: (size: number) => void;
}

export interface RegisterProps<T> {
    title: ReactNode;
    /** The muted note beside the title: "851 of 853" once something narrows the list. */
    count?: ReactNode;
    /** The card header's buttons: Export, then Add. */
    actions?: ReactNode;
    search: string;
    onSearchChange: (value: string) => void;
    searchLabel: string;
    /** Clears the search and every filter; what the empty state offers when the filters, not the search, hid everything. */
    onReset?: () => void;
    /** Filter selects on the search's row. */
    filters?: ReactNode;
    /** The phone's sort control, on the search's row; the caller hides it above 768. */
    sort?: ReactNode;
    status: 'pending' | 'error' | 'ready';
    onRetry: () => void;
    /** Every row that passes the search and filters, in order. Paging happens here. */
    rows: T[];
    /** Rows in the register before search and filters, for the count. */
    total: number;
    /** Changes to the search, filters or sort start again from page one. */
    pageKey: string;
    /** Renders the table for one page of rows. */
    children: (pageRows: T[]) => ReactNode;
    loading?: ReactNode;
    /** Set when the server pages: `rows` is one page and the pager is controlled. */
    paging?: ServerPaging;
    /**
     * The page showing, when the caller keeps it rather than the register: a register whose state
     * rides the URL, so Back from a record lands on the page it left (ALS-224). The caller then
     * owns going back to page one when the search, a filter or the sort changes.
     */
    page?: number;
    onPageChange?: (page: number) => void;
    /**
     * Replaces the words of the "nothing matched" state, for a screen the shared copy does not
     * describe. The app version log is a support lookup rather than a register, and saying so is
     * the difference between the empty state reading as the screen's own and reading as borrowed.
     */
    noMatch?: Partial<Record<keyof typeof REGISTER_COPY.noMatch, string>>;
}

export function Register<T>({
    title,
    count,
    actions,
    search,
    onSearchChange,
    searchLabel,
    onReset,
    filters,
    sort,
    status,
    onRetry,
    rows,
    total,
    pageKey,
    children,
    loading,
    paging,
    page,
    onPageChange,
    noMatch,
}: RegisterProps<T>) {
    const tableTop = useRef<HTMLDivElement>(null);

    // Measured here rather than inside Paged, which only mounts once there are rows to page: the
    // caller that fetches those rows needs the size before it asks for them, not after. tableTop
    // sits above the loading, error and empty branches, so it is in the layout from the first
    // paint and the measurement does not wait on the data.
    const fitted = useFittedRows(tableTop, { rowHeight: ROW_HEIGHT, reserve: RESERVE });

    // The measurement is the register's, the fetch is the caller's, so the size has to travel up.
    // Only a real measurement travels: reporting the fallback first would buy the caller a second
    // request for a page size nothing had measured.
    const reportSize = paging?.onPageSizeChange;
    useEffect(() => {
        if (fitted !== null) reportSize?.(fitted);
    }, [reportSize, fitted]);

    const words = noMatch ? { ...REGISTER_COPY.noMatch, ...noMatch } : REGISTER_COPY.noMatch;

    return (
        <Card floating>
            <CardHeader>
                <CardTitle>{title}</CardTitle>
                {count ? <CardCount>{count}</CardCount> : null}
                {actions ? <CardAction>{actions}</CardAction> : null}
            </CardHeader>
            <CardContent>
                <div className="flex flex-wrap items-center gap-2 pb-3">
                    <SearchInput
                        className="min-w-0 flex-1 md:w-[320px] md:flex-none"
                        placeholder={REGISTER_COPY.searchPlaceholder}
                        aria-label={searchLabel}
                        value={search}
                        onChange={(e) => onSearchChange(e.target.value)}
                    />
                    {sort}
                    {/* On a phone the filters take a row of their own under the search; from 640 up they join its row. */}
                    {filters ? (
                        <div className="flex min-w-0 basis-full flex-col gap-2 sm:contents">
                            {filters}
                        </div>
                    ) : null}
                </div>

                <div ref={tableTop} className="scroll-mt-20" />
                {status === 'pending' ? (
                    (loading ?? <LoadingRows />)
                ) : status === 'error' ? (
                    <Alert variant="destructive">
                        <Unplug />
                        <AlertContent>
                            <AlertTitle>{REGISTER_COPY.failed.title}</AlertTitle>
                            <AlertDescription>{REGISTER_COPY.failed.body}</AlertDescription>
                            <AlertActions>
                                <Button variant="outline" size="sm" onClick={onRetry}>
                                    {REGISTER_COPY.failed.action}
                                </Button>
                            </AlertActions>
                        </AlertContent>
                    </Alert>
                ) : total === 0 ? (
                    <EmptyState
                        icon={<List />}
                        title={REGISTER_COPY.empty.title}
                        body={REGISTER_COPY.empty.body}
                    />
                ) : (paging ? paging.matched : rows.length) === 0 ? (
                    // The search emptied the list, or the filters did: say which and undo that one.
                    <EmptyState
                        icon={<ListFilter />}
                        title={words.title}
                        body={search.trim() ? words.body : words.filtersBody}
                        actions={
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() =>
                                    search.trim() || !onReset ? onSearchChange('') : onReset()
                                }
                            >
                                {search.trim() || !onReset ? words.action : words.filtersAction}
                            </Button>
                        }
                    />
                ) : (
                    <Paged
                        key={paging ? undefined : pageKey}
                        rows={rows}
                        total={total}
                        tableTop={tableTop}
                        pageSize={fitted ?? ROWS_BEFORE_MEASURE}
                        paging={paging}
                        page={page}
                        onPageChange={onPageChange}
                    >
                        {children}
                    </Paged>
                )}
            </CardContent>
        </Card>
    );
}

/** Keyed by the caller so a new search, filter or sort starts on page one, unless the server pages. */
function Paged<T>({
    rows,
    total,
    tableTop,
    pageSize,
    paging,
    page: heldPage,
    onPageChange,
    children,
}: {
    rows: T[];
    total: number;
    tableTop: RefObject<HTMLDivElement | null>;
    /** Measured by the register, which knows the size before there is anything to page. */
    pageSize: number;
    paging?: ServerPaging;
    page?: number;
    onPageChange?: (page: number) => void;
    children: (pageRows: T[]) => ReactNode;
}) {
    const [ownPage, setOwnPage] = useState(1);

    const matched = paging ? paging.matched : rows.length;
    const pages = Math.max(1, Math.ceil(matched / pageSize));
    // Clamped, so a shared link to page 9 of a register that has since shrunk shows the last page.
    const current = Math.min(paging?.page ?? heldPage ?? ownPage, pages);
    const setCurrent = paging?.onPageChange ?? onPageChange ?? setOwnPage;
    return (
        <>
            {children(paging ? rows : rows.slice((current - 1) * pageSize, current * pageSize))}
            <TableFooterBar>
                <span>{REGISTER_COPY.entries(matched, total)}</span>
                {pages > 1 ? (
                    <Pager
                        page={current}
                        pages={pages}
                        onChange={(next) => {
                            setCurrent(next);
                            const top = tableTop.current;
                            if (
                                top &&
                                top.getBoundingClientRect().top < visibleTop(scrollingAncestor(top))
                            ) {
                                top.scrollIntoView({ block: 'start' });
                            }
                        }}
                    />
                ) : null}
            </TableFooterBar>
        </>
    );
}

export function LoadingRows({ label = 'Loading' }: { label?: string }) {
    return (
        <div className="flex flex-col" aria-busy aria-label={label}>
            <Skeleton className="h-[46px] w-full rounded-none" />
            {Array.from({ length: 5 }, (_, i) => (
                <div
                    key={i}
                    className="border-border flex h-[68px] items-center gap-4 border-t px-[18px] first:border-t-0"
                >
                    <div className="flex flex-1 flex-col gap-1.5">
                        <Skeleton className="h-4 w-40" />
                        <Skeleton className="h-3 w-56" />
                    </div>
                    <Skeleton className="hidden h-4 w-24 md:block" />
                    <Skeleton className="h-5 w-16" />
                </div>
            ))}
        </div>
    );
}
